import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Clear } from "@mui/icons-material";

import { publicRecipeService } from "../../../services/publicRecipe-service";
import { AppPage, AppHeader, AppCard, AppScrollArea } from "../../../layout/layout";
import { RecipeCard } from "./_components/recipeCard";

function useDebouncedValue<T>(value: T, delayMs: number) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(t);
    }, [value, delayMs]);
    return debounced;
}

export default function PublicRecipeList() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [query, setQuery] = useState("");
    const debouncedQuery = useDebouncedValue(query, 300);

    const [recipes, setRecipes] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const pageSize = 12;

    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const canLoadMore = recipes.length < total;

    const chipQueries = useMemo(
        () => [
            { label: "Quick & easy", q: "quick" },
            { label: "Vegetarian", q: "vegetarian" },
            { label: "Desserts", q: "dessert" },
            { label: "Sweet", q: "sweet" },
            // add more here later; scrolling will still work
        ],
        []
    );

    useEffect(() => {
        setPage(1);
    }, [debouncedQuery]);

    useEffect(() => {
        let cancelled = false;

        async function run() {
            setLoading(true);
            setError(null);

            try {
                const result: any = await publicRecipeService.getPaged({
                    page,
                    pageSize,
                    q: debouncedQuery,
                });

                if (cancelled) return;

                setTotal(result.total);
                setRecipes((prev) => (page === 1 ? result.items : [...prev, ...result.items]));
            } catch (e: any) {
                if (!cancelled) setError(e?.message ?? "Something went wrong");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        run();
        return () => {
            cancelled = true;
        };
    }, [page, pageSize, debouncedQuery]);

    // Mobile should scroll the whole page, not a nested scroll area
    const ContentWrapper: any = isMobile ? Box : AppScrollArea;

    return (
        <AppPage>
            <AppHeader title="Discover recipes" subtitle="Search by name, ingredients, or category." />

            <AppCard
                sx={{
                    p: { xs: 0, sm: 2.5 },
                    borderRadius: { xs: 0, sm: 1 },
                }}
            >
                {/* Filter area */}
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 2,

                        backgroundColor: "transparent",

                        px: { xs: 0, sm: 0 },
                        pb: { xs: 1, sm: 1.25 },
                        mb: { xs: 1, sm: 1 },
                        borderBottom: isMobile ? "none" : `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Stack spacing={1.25} sx={{ pt: { xs: 0, sm: 1 } }}>
                        <Box sx={{ px: { xs: 0, sm: 0 } }}>
                            <TextField
                                fullWidth
                                size={isMobile ? "small" : "medium"}
                                label="Search recipes"
                                placeholder="e.g. chicken pasta, vegan, breakfast..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                InputProps={{
                                    endAdornment: query ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="clear search"
                                                onClick={() => setQuery("")}
                                                edge="end"
                                                size="small"
                                            >
                                                <Clear />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: isMobile
                                            ? theme.palette.mode === "dark"
                                                ? "rgba(255,255,255,0.04)"
                                                : "rgba(0,0,0,0.03)"
                                            : "transparent",
                                        borderRadius: 999,
                                    },
                                }}
                            />
                        </Box>

                       

                        {/* Chips area */}
                        <Box sx={{ mt: 0.5 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                useFlexGap
                                flexWrap="wrap"
                                sx={{
                                    alignItems: "center",

                                    // keeps it looking neat on mobile
                                    "& .MuiChip-root": {
                                        maxWidth: "100%",
                                    },
                                    "& .MuiChip-label": {
                                        maxWidth: 220, // prevent super long chips from breaking layout
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    },
                                }}
                            >
                                {chipQueries.map((c) => (
                                    <Chip
                                        key={c.label}
                                        label={c.label}
                                        onClick={() => setQuery(c.q)}
                                        variant={query === c.q ? "filled" : "outlined"}
                                        sx={(theme) => ({
                                            borderColor: theme.palette.primary.main,
                                            color: theme.palette.text.primary,
                                        })}
                                    />
                                ))}
                            </Stack>
                        </Box>

                    </Stack>
                </Box>

                {/* Recipes list */}
                <ContentWrapper sx={{ mt: 0.5 }}>
                    <Box sx={{ px: { xs: 0, sm: 0 } }}>
                        {error && (
                            <Typography color="error" sx={{ mb: 2 }}>
                                {error}
                            </Typography>
                        )}

                        <Grid container spacing={{ xs: 1.25, sm: 2 }}>
                            {recipes.map((recipe) => (
                                <Grid key={recipe.id} size={{ xs: 12, sm: 6, md: 4 }}>
                                    <RecipeCard recipe={recipe} />
                                </Grid>
                            ))}

                            {!loading && recipes.length === 0 && (
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                                        {debouncedQuery.trim()
                                            ? "No recipes found. Try another search."
                                            : "Start typing above to search recipes."}
                                    </Typography>
                                </Grid>
                            )}

                            {loading && (
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                                        Loading...
                                    </Typography>
                                </Grid>
                            )}

                            {!loading && canLoadMore && (
                                <Grid size={{ xs: 12 }}>
                                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                        <Button
                                            variant="contained"
                                            onClick={() => setPage((p) => p + 1)}
                                            fullWidth={isMobile}
                                            sx={{
                                                maxWidth: isMobile ? "100%" : 260,
                                                borderRadius: 999,
                                                py: 1.1,
                                                fontWeight: 800,
                                            }}
                                        >
                                            Load more
                                        </Button>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </ContentWrapper>
            </AppCard>
        </AppPage>
    );
}
